// File: frontend/src/components/EnhancedSettingsModal.js

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, X, Edit2, Trash2, Monitor, AlertTriangle, LogOut, Download, Moon, Sun, RefreshCw, Zap, Palette, Check } from 'lucide-react';
import { useAnimations } from '../context/AnimationContext';
import { useTheme } from '../context/ThemeContext';
import { useConnectivity } from '../context/ConnectivityContext';
import colorPresets from '../constants/colorPresets';
import GradientColorPicker from './GradientColorPicker';
import { getModalConfig } from '../constants/layoutConfigs';
import * as DisplayControl from '../utils/displayControl';
import * as StatusBar from '../utils/statusBar';
import { downloadExport } from '../utils/exportData';
import DeleteAccountModal from './DeleteAccountModal';
import ToggleSwitch from './ToggleSwitch';
import { habitFill } from '../utils/colorUtils';

// Check if running in Electron
const isElectron = () => {
  return typeof window !== 'undefined' && window.electronAPI;
};

function EnhancedSettingsModal({ habits, onAdd, onUpdate, onDelete, onClose, apiUrl, onLogout }) {
  const [newHabit, setNewHabit] = useState({ name: '', color: colorPresets[0].gradient, weekly_goal: 7 });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [modalConfig, setModalConfig] = useState(() => getModalConfig(window.innerWidth));
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // 'idle' | 'preparing' | 'done' | 'error'
  const [exportState, setExportState] = useState('idle');
  const [exportError, setExportError] = useState(null);
  const { isOnline, pendingCount } = useConnectivity();

  const handleExport = async () => {
    if (exportState === 'preparing') return;
    if (pendingCount > 0) {
      const ok = window.confirm(
        `You have ${pendingCount} unsynced change${pendingCount === 1 ? '' : 's'}. ` +
        `They may not be in the export yet. Continue anyway?`
      );
      if (!ok) return;
    }
    setExportState('preparing');
    setExportError(null);
    try {
      await downloadExport(apiUrl);
      setExportState('done');
      setTimeout(() => setExportState('idle'), 3000);
    } catch (e) {
      setExportState('error');
      setExportError(e.message || 'Export failed');
      setTimeout(() => setExportState('idle'), 5000);
    }
  };

  // Animation settings
  const { perfMode, setPerfMode } = useAnimations();

  // Theme preference (light / dark / auto).
  const { mode: themeMode, setMode: setThemeMode } = useTheme();

  // Fullscreen mode (Android only). ON = fullscreen (system bars hidden).
  // Default is OFF (bars visible, traditional layout).
  const fullscreenSupported = StatusBar.isSupported();
  const [fullscreenEnabled, setFullscreenEnabled] = useState(() => !StatusBar.getStoredVisibility());
  const toggleFullscreen = async (enabled) => {
    setFullscreenEnabled(enabled);
    // `setVisible(false)` hides the bars (enters fullscreen).
    await StatusBar.setVisible(!enabled);
  };

  // Display schedule state
  const [displayControlSupported, setDisplayControlSupported] = useState(false);
  const [displaySchedule, setDisplaySchedule] = useState({
    enabled: false,
    startHour: 22,
    startMinute: 0,
    endHour: 6,
    endMinute: 0
  });
  const [displayMode, setDisplayMode] = useState(() => {
    return localStorage.getItem('steadily_display_mode') || 'brightness';
  });
  const [displayTestStatus, setDisplayTestStatus] = useState(null); // null, 'testing-off', 'testing-on', 'success', 'error'
  const [displayTestError, setDisplayTestError] = useState(null);

  // Auto-update state (Electron only)
  const [autoUpdateEnabled, setAutoUpdateEnabled] = useState(false);
  const [updateChannel, setUpdateChannel] = useState('stable');
  const [appVersion, setAppVersion] = useState('');
  const [checkingForUpdates, setCheckingForUpdates] = useState(false);

  // Load display schedule on mount
  useEffect(() => {
    console.log('[SettingsModal] Loading display settings...');
    const loadDisplaySettings = async () => {
      try {
        console.log('[SettingsModal] Calling isDisplayControlSupported...');
        const supported = await DisplayControl.isDisplayControlSupported();
        console.log('[SettingsModal] Display control supported:', supported);
        setDisplayControlSupported(supported);

        if (supported) {
          // Get platform info (now async for root detection)
          await DisplayControl.getPlatformInfo();
          // setPlatformInfo(info); // Removed unused setter

          // Load schedule
          const schedule = await DisplayControl.getDisplaySchedule();
          if (schedule) {
            setDisplaySchedule(schedule);
          }
        }
      } catch (error) {
        console.error('Error loading display settings:', error);
      }
    };

    loadDisplaySettings();
  }, []);

  // Load auto-update settings on mount (Electron only)
  useEffect(() => {
    const loadUpdateSettings = async () => {
      if (!isElectron()) return;

      try {
        const enabled = await window.electronAPI.isAutoUpdateEnabled();
        setAutoUpdateEnabled(enabled);

        const channel = await window.electronAPI.getUpdateChannel();
        setUpdateChannel(channel);

        const version = await window.electronAPI.getAppVersion();
        setAppVersion(version);
      } catch (error) {
        console.error('Error loading update settings:', error);
      }
    };

    loadUpdateSettings();
  }, []);

  // Update modal config on resize
  useEffect(() => {
    const handleResize = () => {
      setModalConfig(getModalConfig(window.innerWidth));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleEdit = (habit) => {
    setEditingId(habit.id);
    setEditForm({
      name: habit.name,
      color: habit.color,
      weekly_goal: habit.weekly_goal
    });
  };

  const handleSaveEdit = () => {
    onUpdate(editingId, editForm.name, editForm.color, editForm.weekly_goal);
    setEditingId(null);
    setEditForm({});
  };

  const handleDisplayScheduleChange = async (field, value) => {
    const newSchedule = { ...displaySchedule, [field]: value };
    setDisplaySchedule(newSchedule);

    // Save using unified API
    try {
      await DisplayControl.setDisplaySchedule(newSchedule);
    } catch (error) {
      console.error('Error saving display schedule:', error);
    }
  };

  const handleDisplayModeChange = (mode) => {
    setDisplayMode(mode);
    localStorage.setItem('steadily_display_mode', mode);
  };

  const handleUpdateChannelChange = async (channel) => {
    if (!isElectron()) return;

    setUpdateChannel(channel);
    try {
      await window.electronAPI.setUpdateChannel(channel);
    } catch (error) {
      console.error('Error setting update channel:', error);
    }
  };

  const handleCheckForUpdates = async () => {
    if (!isElectron()) return;

    setCheckingForUpdates(true);
    try {
      await window.electronAPI.checkForUpdates();
    } catch (error) {
      console.error('Error checking for updates:', error);
    } finally {
      // Reset after 3 seconds
      setTimeout(() => setCheckingForUpdates(false), 3000);
    }
  };

  const handleTestDisplayOff = async () => {
    setDisplayTestStatus('testing-off');
    setDisplayTestError(null);
    try {
      const result = displayMode === 'screen'
        ? await DisplayControl.turnScreenOff()
        : await DisplayControl.turnDisplayOff();
      if (result.success) {
        setDisplayTestStatus('success');
        // Auto wake after 3 seconds for testing
        setTimeout(async () => {
          displayMode === 'screen'
            ? await DisplayControl.turnScreenOn()
            : await DisplayControl.turnDisplayOn();
          setDisplayTestStatus(null);
        }, 3000);
      } else {
        setDisplayTestStatus('error');
        setDisplayTestError(result.error || 'Failed to turn display off');
      }
    } catch (error) {
      setDisplayTestStatus('error');
      setDisplayTestError(error.message);
    }
  };

  const handleTestDisplayOn = async () => {
    setDisplayTestStatus('testing-on');
    setDisplayTestError(null);
    try {
      const result = displayMode === 'screen'
        ? await DisplayControl.turnScreenOn()
        : await DisplayControl.turnDisplayOn();
      if (result.success) {
        setDisplayTestStatus('success');
        setTimeout(() => setDisplayTestStatus(null), 2000);
      } else {
        setDisplayTestStatus('error');
        setDisplayTestError(result.error || 'Failed to turn display on');
      }
    } catch (error) {
      setDisplayTestStatus('error');
      setDisplayTestError(error.message);
    }
  };

  // Determine modal container classes based on config.
  // Backdrop color is applied via inline style (rgba) because Chrome 53 can't
  // parse `bg-[var(--color-xxx)]/opacity` tailwind modifiers, and `backdrop-blur`
  // is also unsupported there — without a fallback the scrim is invisible and
  // the modal has no visual separation from the page behind it.
  const getModalContainerClasses = () => {
    if (modalConfig.type === 'fullscreen') {
      return 'fixed top-0 left-0 right-0 bottom-0 backdrop-blur-sm z-50 animate-fadeIn';
    } else if (modalConfig.type === 'slide-in') {
      return 'fixed top-0 left-0 right-0 bottom-0 backdrop-blur-sm z-50 animate-fadeIn flex justify-end';
    } else {
      return 'fixed top-0 left-0 right-0 bottom-0 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fadeIn';
    }
  };

  // Inline backdrop color — pulls from the theme scrim token so dark mode
  // gets a darker, more opaque backdrop while light mode keeps the ~40%
  // ink-main wash. Inlined (not Tailwind) so Chrome 53 actually renders it.
  const modalContainerStyle = { backgroundColor: 'var(--color-scrim)' };

  // Layered drop shadow: a tight ambient + a larger soft falloff. `shadow-2xl`
  // alone (single `box-shadow: 0 25px 50px -12px rgb(0 0 0 / 0.25)`) gets lost
  // on a paper-cream modal over a paper-cream page — the modal looks flush.
  const modalContentStyle = {
    boxShadow:
      '0 1px 3px rgba(43, 38, 38, 0.12), ' +
      '0 10px 25px -5px rgba(43, 38, 38, 0.18), ' +
      '0 30px 60px -15px rgba(43, 38, 38, 0.28)',
  };

  const getModalContentClasses = () => {
    // Common styles for the "paper" look
    const paperStyles = "bg-[var(--color-paper-warm)] text-[var(--color-ink-main)]";

    if (modalConfig.type === 'fullscreen') {
      return `${paperStyles} w-full h-full overflow-y-auto animate-slideUpFromBottom flex flex-col`;
    } else if (modalConfig.type === 'slide-in') {
      return `${paperStyles} ${modalConfig.width} h-full overflow-y-auto border-l border-[var(--color-ink-subtle)]/10 animate-slideInRight flex flex-col`;
    } else {
      return `${paperStyles} rounded-2xl p-10 w-full ${modalConfig.width} ${modalConfig.height} overflow-y-auto border border-[var(--color-ink-subtle)]/10 animate-slideUp`;
    }
  };


  return (
    <div className={getModalContainerClasses()} style={modalContainerStyle} onClick={onClose}>
      <div className={getModalContentClasses()} style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header band. Was `sticky top-0 bg-paper-warm/95 backdrop-blur`,
            but on Chrome 53 (Android 6) both `position: sticky` and
            `backdrop-filter` no-op, so the alpha-blurred sticky header
            rendered as a transparent static element that scrolled with the
            body. Switched to a plain opaque header — non-sticky, fully
            opaque paper background — so it's at least visible while the
            user scrolls the modal body. */}
        <div className={`flex items-center justify-between ${modalConfig.type === 'fullscreen' || modalConfig.type === 'slide-in' ? 'p-6 border-b border-[var(--color-ink-subtle)]/10 bg-[var(--color-paper-warm)] z-10' : 'mb-8'}`}>
          <div>
            <h2 className={`font-serif font-bold text-[var(--color-ink-main)] ${modalConfig.type === 'fullscreen' || modalConfig.type === 'slide-in' ? 'text-2xl' : 'text-3xl'}`}>Settings</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-[var(--color-ink-subtle)]/10 transition-colors"
          >
            <X className="w-6 h-6 text-[var(--color-ink-light)]" />
          </button>
        </div>

        {/* Content wrapper for slide-in and fullscreen */}
        <div className={`${modalConfig.type === 'fullscreen' || modalConfig.type === 'slide-in' ? 'flex-1 overflow-y-auto p-6' : ''}`}>



          {/* Habits Section */}
          <div className="mb-12">
            <h3 className="font-serif font-bold text-xl text-[var(--color-ink-main)] mb-5 flex items-center space-x-2">
              Your Habits
            </h3>

            {/* Add New Habit */}
            <div className="bg-[var(--color-surface-card)] rounded-xl p-5 border border-[var(--color-divider)] shadow-sm mb-6">
              {/* Row 1: name + goal + add button.
                  `min-w-0` on the input lets flex-1 actually shrink — otherwise
                  `min-width: auto` holds it at placeholder-intrinsic width and
                  pushes the Add button outside the card. */}
              <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-3">
                <input
                  type="text"
                  placeholder="New habit name..."
                  value={newHabit.name}
                  onChange={(e) => setNewHabit({ ...newHabit, name: e.target.value })}
                  className="flex-1 min-w-0 px-4 py-2.5 bg-[var(--color-paper-bg)] rounded-lg border border-[var(--color-ink-subtle)]/20 focus:border-[var(--color-accent-warm)] focus:outline-none font-sans"
                />
                <select
                  value={newHabit.weekly_goal}
                  onChange={(e) => setNewHabit({ ...newHabit, weekly_goal: parseInt(e.target.value) })}
                  className="w-24 px-3 py-2.5 bg-[var(--color-paper-bg)] rounded-lg border border-[var(--color-ink-subtle)]/20 focus:border-[var(--color-accent-warm)] focus:outline-none font-sans text-center"
                >
                  {[1, 2, 3, 4, 5, 6, 7].map(n => (
                    <option key={n} value={n}>{n} / wk</option>
                  ))}
                </select>
                <button
                  onClick={() => {
                    if (newHabit.name) {
                      onAdd(newHabit.name, newHabit.color, newHabit.weekly_goal);
                      setNewHabit({ name: '', color: colorPresets[0].gradient, weekly_goal: 7 });
                    }
                  }}
                  className="px-5 py-2.5 bg-[var(--color-ink-main)] text-[var(--color-paper-bg)] rounded-lg font-medium hover:opacity-90 transition-opacity flex items-center justify-center space-x-2"
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">Add</span>
                </button>
              </div>
              {/* Row 2: color swatches — shared with StarterHabitsModal. */}
              <GradientColorPicker
                value={newHabit.color}
                onChange={(color) => setNewHabit({ ...newHabit, color })}
                className="mt-5"
              />
            </div>

            {/* Habit List */}
            <div className="space-y-4">
              {habits.map(habit => (
                <div key={habit.id} className="bg-[var(--color-surface-card)] rounded-xl px-5 py-4 border border-[var(--color-divider)] shadow-sm flex items-center justify-between group hover:border-[var(--color-ink-subtle)]/30 transition-colors">
                  {editingId === habit.id ? (
                    <div className="flex-1 space-y-3">
                      {/* Row 1: name + goal + save/cancel */}
                      <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
                        <input
                          type="text"
                          value={editForm.name}
                          onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                          className="flex-1 min-w-0 px-3 py-2 bg-[var(--color-paper-bg)] rounded-lg border border-[var(--color-ink-subtle)]/20 focus:border-[var(--color-accent-warm)] focus:outline-none"
                        />
                        <div className="flex space-x-2">
                          <input
                            type="number"
                            min="1"
                            max="7"
                            value={editForm.weekly_goal}
                            onChange={(e) => setEditForm({ ...editForm, weekly_goal: parseInt(e.target.value) })}
                            className="w-16 px-2 py-2 bg-[var(--color-paper-bg)] rounded-lg border border-[var(--color-ink-subtle)]/20 text-center"
                          />
                          <button onClick={handleSaveEdit} className="px-3 py-1 bg-green-600 text-white rounded-lg text-sm">Save</button>
                          <button onClick={() => setEditingId(null)} className="px-3 py-1 bg-[var(--color-surface-input)] text-[var(--color-ink-light)] rounded-lg text-sm">Cancel</button>
                        </div>
                      </div>
                      {/* Row 2: color swatches — same pattern as the Add form */}
                      <GradientColorPicker
                        value={editForm.color}
                        onChange={(color) => setEditForm({ ...editForm, color })}
                      />
                    </div>
                  ) : (
                    <>
                      {/* gap → space-x: flex `gap` needs Chrome 84+, silently fails on Chrome 53 and
                          leaves the dot hugging the name. `space-x-*` uses sibling margin, works everywhere.
                          `ml-4` on the outer wrapper gives the dot visible breathing room from the card's
                          left padding so it doesn't look like it's bleeding into the edge. */}
                      <div className="flex items-center space-x-4 min-w-0 ml-1">
                        <div className={`w-9 h-9 rounded-full ${habitFill(habit.color).className} shadow-sm flex-shrink-0`} style={habitFill(habit.color).style} aria-hidden="true"></div>
                        <div className="min-w-0 space-y-0.5">
                          <h4 className="font-serif font-medium text-[var(--color-ink-main)]">{habit.name}</h4>
                          <p className="text-xs text-[var(--color-ink-light)] font-sans">Goal: {habit.weekly_goal} days/week</p>
                        </div>
                      </div>
                      <div className="flex space-x-2 opacity-50 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => handleEdit(habit)} className="p-2 hover:bg-[var(--color-surface-input)] rounded-lg text-[var(--color-ink-light)]">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            if (window.confirm(`Delete "${habit.name}"?`)) {
                              onDelete(habit.id);
                            }
                          }}
                          className="p-2 hover:bg-[var(--color-hover-danger)] rounded-lg text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Display Sleep Schedule - Only show in Electron */}
          {displayControlSupported && (
            <div className="mb-12">
              <h3 className="font-serif font-bold text-xl text-[var(--color-ink-main)] mb-5 flex items-center space-x-2">
                <Monitor className="w-5 h-5 text-[var(--color-ink-light)]" />
                Display Sleep Schedule
              </h3>
              <div className="bg-[var(--color-surface-card)] rounded-xl p-7 border border-[var(--color-divider)] shadow-sm">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <div className="font-medium text-[var(--color-ink-main)]">Enable Schedule</div>
                    <div className="text-xs text-[var(--color-ink-light)]">Turn display off automatically</div>
                  </div>
                  <ToggleSwitch
                    checked={displaySchedule.enabled}
                    onChange={(v) => handleDisplayScheduleChange('enabled', v)}
                    ariaLabel={displaySchedule.enabled ? 'Disable display schedule' : 'Enable display schedule'}
                  />
                </div>

                <div className={`flex flex-col space-y-4 sm:flex-row sm:space-x-4 sm:space-y-0 ${displaySchedule.enabled ? '' : 'opacity-50'}`}>
                  <div className="sm:flex-1">
                    <label className="block text-xs font-medium text-[var(--color-ink-light)] mb-1">Turn Off At</label>
                    <div className="flex items-center space-x-1">
                      <select
                        value={displaySchedule.startHour % 12 || 12}
                        disabled={!displaySchedule.enabled}
                        onChange={(e) => {
                          const hour12 = parseInt(e.target.value);
                          const isPM = displaySchedule.startHour >= 12;
                          const hour24 = isPM ? (hour12 === 12 ? 12 : hour12 + 12) : (hour12 === 12 ? 0 : hour12);
                          handleDisplayScheduleChange('startHour', hour24);
                        }}
                        className="bg-[var(--color-paper-bg)] border border-[var(--color-ink-subtle)]/20 rounded p-2 text-sm w-14 disabled:cursor-not-allowed"
                      >
                        {Array.from({ length: 12 }, (_, i) => i + 1).map(h => <option key={h} value={h}>{h}</option>)}
                      </select>
                      <span className="flex items-center">:</span>
                      <select
                        value={displaySchedule.startMinute}
                        disabled={!displaySchedule.enabled}
                        onChange={(e) => handleDisplayScheduleChange('startMinute', parseInt(e.target.value))}
                        className="bg-[var(--color-paper-bg)] border border-[var(--color-ink-subtle)]/20 rounded p-2 text-sm w-14 disabled:cursor-not-allowed"
                      >
                        {Array.from({ length: 60 }, (_, i) => <option key={i} value={i}>{i.toString().padStart(2, '0')}</option>)}
                      </select>
                      <select
                        value={displaySchedule.startHour >= 12 ? 'PM' : 'AM'}
                        disabled={!displaySchedule.enabled}
                        onChange={(e) => {
                          const isPM = e.target.value === 'PM';
                          const currentHour12 = displaySchedule.startHour % 12 || 12;
                          const hour24 = isPM ? (currentHour12 === 12 ? 12 : currentHour12 + 12) : (currentHour12 === 12 ? 0 : currentHour12);
                          handleDisplayScheduleChange('startHour', hour24);
                        }}
                        className="bg-[var(--color-paper-bg)] border border-[var(--color-ink-subtle)]/20 rounded p-2 text-sm w-16 disabled:cursor-not-allowed"
                      >
                        <option value="AM">AM</option>
                        <option value="PM">PM</option>
                      </select>
                    </div>
                  </div>
                  <div className="sm:flex-1">
                    <label className="block text-xs font-medium text-[var(--color-ink-light)] mb-1">Turn On At</label>
                    <div className="flex items-center space-x-1">
                      <select
                        value={displaySchedule.endHour % 12 || 12}
                        disabled={!displaySchedule.enabled}
                        onChange={(e) => {
                          const hour12 = parseInt(e.target.value);
                          const isPM = displaySchedule.endHour >= 12;
                          const hour24 = isPM ? (hour12 === 12 ? 12 : hour12 + 12) : (hour12 === 12 ? 0 : hour12);
                          handleDisplayScheduleChange('endHour', hour24);
                        }}
                        className="bg-[var(--color-paper-bg)] border border-[var(--color-ink-subtle)]/20 rounded p-2 text-sm w-14 disabled:cursor-not-allowed"
                      >
                        {Array.from({ length: 12 }, (_, i) => i + 1).map(h => <option key={h} value={h}>{h}</option>)}
                      </select>
                      <span className="flex items-center">:</span>
                      <select
                        value={displaySchedule.endMinute}
                        disabled={!displaySchedule.enabled}
                        onChange={(e) => handleDisplayScheduleChange('endMinute', parseInt(e.target.value))}
                        className="bg-[var(--color-paper-bg)] border border-[var(--color-ink-subtle)]/20 rounded p-2 text-sm w-14 disabled:cursor-not-allowed"
                      >
                        {Array.from({ length: 60 }, (_, i) => <option key={i} value={i}>{i.toString().padStart(2, '0')}</option>)}
                      </select>
                      <select
                        value={displaySchedule.endHour >= 12 ? 'PM' : 'AM'}
                        disabled={!displaySchedule.enabled}
                        onChange={(e) => {
                          const isPM = e.target.value === 'PM';
                          const currentHour12 = displaySchedule.endHour % 12 || 12;
                          const hour24 = isPM ? (currentHour12 === 12 ? 12 : currentHour12 + 12) : (currentHour12 === 12 ? 0 : currentHour12);
                          handleDisplayScheduleChange('endHour', hour24);
                        }}
                        className="bg-[var(--color-paper-bg)] border border-[var(--color-ink-subtle)]/20 rounded p-2 text-sm w-16 disabled:cursor-not-allowed"
                      >
                        <option value="AM">AM</option>
                        <option value="PM">PM</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Display Mode Selection */}
                <div className="mt-6 pt-4 border-t border-[var(--color-ink-subtle)]/10">
                  <label className="block text-xs font-medium text-[var(--color-ink-light)] mb-2">Display Control Mode</label>
                  <div className="flex bg-[var(--color-surface-input)] rounded-lg p-1 mb-3">
                    <button
                      onClick={() => handleDisplayModeChange('brightness')}
                      className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-all ${
                        displayMode === 'brightness'
                          ? 'bg-[var(--color-surface-card)] text-[var(--color-ink-main)] shadow-sm'
                          : 'text-[var(--color-ink-light)] hover:text-[var(--color-ink-main)]'
                      }`}
                    >
                      Dim Brightness
                    </button>
                    <button
                      onClick={() => handleDisplayModeChange('screen')}
                      className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-all ${
                        displayMode === 'screen'
                          ? 'bg-[var(--color-surface-card)] text-[var(--color-ink-main)] shadow-sm'
                          : 'text-[var(--color-ink-light)] hover:text-[var(--color-ink-main)]'
                      }`}
                    >
                      Screen Off
                    </button>
                  </div>
                  <p className="text-xs text-[var(--color-ink-subtle)] mb-4">
                    {displayMode === 'brightness'
                      ? 'Dims screen to minimum brightness with black overlay (works on all devices)'
                      : 'Turns screen completely off (requires rooted device)'}
                  </p>
                </div>

                {/* Test Display Controls */}
                <div className="pt-4 border-t border-[var(--color-ink-subtle)]/10">
                  <label className="block text-xs font-medium text-[var(--color-ink-light)] mb-2">Test Display Control</label>
                  <div className="flex space-x-2">
                    <button
                      onClick={handleTestDisplayOff}
                      disabled={displayTestStatus === 'testing-off'}
                      className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-[var(--color-ink-main)] text-[var(--color-paper-bg)] rounded-lg hover:opacity-90 disabled:opacity-50 transition-colors text-sm"
                    >
                      <Moon className="w-4 h-4" />
                      {displayTestStatus === 'testing-off' ? 'Turning Off...' : 'Test Off (3s)'}
                    </button>
                    <button
                      onClick={handleTestDisplayOn}
                      disabled={displayTestStatus === 'testing-on'}
                      className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-400 disabled:opacity-50 transition-colors text-sm"
                    >
                      <Sun className="w-4 h-4" />
                      {displayTestStatus === 'testing-on' ? 'Turning On...' : 'Test On'}
                    </button>
                  </div>
                  {displayTestStatus === 'success' && (
                    <p className="text-xs text-green-600 mt-2">Display control working!</p>
                  )}
                  {displayTestStatus === 'error' && (
                    <p className="text-xs text-red-500 mt-2">Error: {displayTestError}</p>
                  )}
                  <p className="text-xs text-[var(--color-ink-subtle)] mt-2 italic">
                    "Test Off" will turn off display for 3 seconds then auto-wake.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Appearance Section */}
          <div className="mb-12">
            <h3 className="font-serif font-bold text-xl text-[var(--color-ink-main)] mb-5 flex items-center space-x-2">
              <Palette className="w-5 h-5 text-[var(--color-ink-light)]" />
              Appearance
            </h3>
            <div className="bg-[var(--color-surface-card)] rounded-xl p-7 border border-[var(--color-divider)] shadow-sm">
              <label className="block text-xs font-medium text-[var(--color-ink-light)] mb-2">Theme</label>
              <div className="flex bg-[var(--color-surface-input)] rounded-lg p-1">
                <button
                  type="button"
                  onClick={() => setThemeMode('light')}
                  aria-pressed={themeMode === 'light'}
                  className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all ${
                    themeMode === 'light'
                      ? 'bg-[var(--color-surface-card)] text-[var(--color-ink-main)] shadow-sm'
                      : 'text-[var(--color-ink-light)] hover:text-[var(--color-ink-main)]'
                  }`}
                >
                  <Sun className="w-4 h-4" />
                  Light
                </button>
                <button
                  type="button"
                  onClick={() => setThemeMode('auto')}
                  aria-pressed={themeMode === 'auto'}
                  className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all ${
                    themeMode === 'auto'
                      ? 'bg-[var(--color-surface-card)] text-[var(--color-ink-main)] shadow-sm'
                      : 'text-[var(--color-ink-light)] hover:text-[var(--color-ink-main)]'
                  }`}
                >
                  <Monitor className="w-4 h-4" />
                  Auto
                </button>
                <button
                  type="button"
                  onClick={() => setThemeMode('dark')}
                  aria-pressed={themeMode === 'dark'}
                  className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all ${
                    themeMode === 'dark'
                      ? 'bg-[var(--color-surface-card)] text-[var(--color-ink-main)] shadow-sm'
                      : 'text-[var(--color-ink-light)] hover:text-[var(--color-ink-main)]'
                  }`}
                >
                  <Moon className="w-4 h-4" />
                  Dark
                </button>
              </div>
              <p className="text-xs text-[var(--color-ink-subtle)] mt-3">
                {themeMode === 'auto'
                  ? 'Follows your device’s system setting.'
                  : themeMode === 'dark'
                    ? 'Dark paper, warm ink.'
                    : 'Light paper, dark ink.'}
              </p>
            </div>
          </div>

          {/* Performance Section */}
          <div className="mb-12">
            <h3 className="font-serif font-bold text-xl text-[var(--color-ink-main)] mb-5 flex items-center space-x-2">
              <Zap className="w-5 h-5 text-[var(--color-ink-light)]" />
              Performance
            </h3>
            <div className="bg-[var(--color-surface-card)] rounded-xl p-7 border border-[var(--color-divider)] shadow-sm space-y-5">
              {/* Unified performance mode. A single choice drives both the
                  device-tier CSS class (.perf-{low|high}) and the
                  reduce-animations kill-switch. "Auto" detects device
                  capability (Fire tablets, Android 6/7, low-RAM/few-core
                  devices → low tier). See AnimationContext for the mapping. */}
              <div>
                <label className="block text-xs font-medium text-[var(--color-ink-light)] mb-2">Performance mode</label>
                <div className="flex bg-[var(--color-surface-input)] rounded-lg p-1">
                  {[
                    { key: 'auto', label: 'Auto' },
                    { key: 'pretty', label: 'Pretty' },
                    { key: 'smooth', label: 'Smooth' },
                    { key: 'fastest', label: 'Fastest' },
                  ].map(opt => {
                    const active = perfMode === opt.key;
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => setPerfMode(opt.key)}
                        aria-pressed={active}
                        className={`flex-1 px-2 py-2 rounded-md text-sm font-medium transition-all ${
                          active
                            ? 'bg-[var(--color-surface-card)] text-[var(--color-ink-main)] shadow-sm'
                            : 'text-[var(--color-ink-light)] hover:text-[var(--color-ink-main)]'
                        }`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-[var(--color-ink-subtle)] mt-3">
                  {perfMode === 'auto'
                    ? 'Auto — adapts to your device.'
                    : perfMode === 'pretty'
                      ? 'Full visual quality. Best on capable devices.'
                      : perfMode === 'smooth'
                        ? 'Strips the heaviest effects for a smoother framerate.'
                        : 'Minimal animations for the highest performance.'}
                </p>
              </div>

              {/* Android-only: fullscreen mode. Hides both the top status bar
                  and bottom navigation/taskbar; swipe from an edge to peek. */}
              {fullscreenSupported && (
                <div className="flex items-center justify-between pt-5 border-t border-[var(--color-ink-subtle)]/10">
                  <div className="pr-4">
                    <div className="font-medium text-[var(--color-ink-main)]">Fullscreen</div>
                    <div className="text-xs text-[var(--color-ink-light)]">Hide the Android status bar and navigation bar. Swipe from an edge to peek when hidden.</div>
                  </div>
                  <ToggleSwitch
                    checked={fullscreenEnabled}
                    onChange={toggleFullscreen}
                    ariaLabel={fullscreenEnabled ? 'Exit fullscreen' : 'Enter fullscreen'}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Updates Section - Only show in Electron with auto-update enabled */}
          {isElectron() && autoUpdateEnabled && (
            <div className="mb-12">
              <h3 className="font-serif font-bold text-xl text-[var(--color-ink-main)] mb-5 flex items-center space-x-2">
                <Download className="w-5 h-5 text-[var(--color-ink-light)]" />
                Updates
              </h3>
              <div className="bg-[var(--color-surface-card)] rounded-xl p-7 border border-[var(--color-divider)] shadow-sm space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-[var(--color-ink-main)]">Update Channel</div>
                    <div className="text-xs text-[var(--color-ink-light)]">
                      {updateChannel === 'beta' ? 'Get early access to new features' : 'Stable releases only'}
                    </div>
                  </div>
                  <div className="flex bg-[var(--color-surface-input)] rounded-lg p-1">
                    <button
                      onClick={() => handleUpdateChannelChange('stable')}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                        updateChannel === 'stable'
                          ? 'bg-[var(--color-surface-card)] text-[var(--color-ink-main)] shadow-sm'
                          : 'text-[var(--color-ink-light)] hover:text-[var(--color-ink-main)]'
                      }`}
                    >
                      Stable
                    </button>
                    <button
                      onClick={() => handleUpdateChannelChange('beta')}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                        updateChannel === 'beta'
                          ? 'bg-[var(--color-surface-card)] text-[var(--color-ink-main)] shadow-sm'
                          : 'text-[var(--color-ink-light)] hover:text-[var(--color-ink-main)]'
                      }`}
                    >
                      Beta
                    </button>
                  </div>
                </div>

                <div className="pt-4 border-t border-[var(--color-ink-subtle)]/10">
                  <button
                    onClick={handleCheckForUpdates}
                    disabled={checkingForUpdates}
                    className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-[var(--color-ink-main)] text-[var(--color-paper-bg)] rounded-lg hover:opacity-90 disabled:opacity-50 transition-all text-sm font-medium"
                  >
                    <RefreshCw className={`w-4 h-4 ${checkingForUpdates ? 'animate-spin' : ''}`} />
                    {checkingForUpdates ? 'Checking for Updates...' : 'Check for Updates'}
                  </button>
                  <p className="text-xs text-[var(--color-ink-subtle)] mt-2 text-center">
                    Updates check automatically every 10 minutes
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Account Management Section */}
          <div className="mt-12 pt-8 border-t border-[var(--color-ink-subtle)]/10">
            <h3 className="font-serif font-bold text-xl text-[var(--color-ink-main)] mb-5">Account</h3>

            <div className="space-y-3">
              {/* Export My Data — safe action, lives above destructive ones so a
                  panicked user can grab their data before Delete Account. */}
              <button
                onClick={handleExport}
                disabled={!isOnline || exportState === 'preparing'}
                className="w-full px-6 py-3 bg-[var(--color-surface-input)] text-[var(--color-ink-main)] rounded-xl hover:bg-[var(--color-divider)] transition-all font-medium flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                title={!isOnline ? "You're offline — export needs a connection" : undefined}
              >
                {exportState === 'done' ? (
                  <>
                    <Check className="w-4 h-4" />
                    <span>Downloaded</span>
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    <span>{exportState === 'preparing' ? 'Preparing…' : 'Export My Data'}</span>
                  </>
                )}
              </button>
              {exportState === 'error' && exportError && (
                <p className="text-xs text-red-600 text-center font-medium" role="alert">
                  {exportError}. Try again in a moment.
                </p>
              )}
              {!isOnline && exportState === 'idle' && (
                <p className="text-xs text-[var(--color-ink-light)] text-center">
                  You're offline — connect to export your data.
                </p>
              )}

              {/* Logout Button - Moved here */}
              <button
                onClick={() => {
                  onClose();
                  onLogout && onLogout();
                }}
                className="w-full px-6 py-3 bg-[var(--color-surface-input)] text-[var(--color-ink-main)] rounded-xl hover:bg-[var(--color-divider)] transition-all font-medium flex items-center justify-center space-x-2"
              >
                <LogOut className="w-4 h-4" />
                Sign Out
              </button>

              <button
                onClick={() => setShowDeleteModal(true)}
                className="w-full px-4 py-2 bg-transparent text-red-500 rounded-lg hover:bg-[var(--color-hover-danger)] transition-all text-xs font-medium flex items-center justify-center space-x-2"
              >
                <AlertTriangle className="w-3 h-3" />
                Delete Account
              </button>
            </div>

            {/* Legal links */}
            <div className="mt-6 flex items-center justify-center gap-4 text-xs text-[var(--color-ink-light)] font-serif">
              <Link
                to="/privacy"
                onClick={onClose}
                className="hover:text-[var(--color-ink-main)] transition-colors"
              >
                Privacy Policy
              </Link>
              <span className="text-[var(--color-ink-subtle)]/40" aria-hidden="true">·</span>
              <Link
                to="/terms"
                onClick={onClose}
                className="hover:text-[var(--color-ink-main)] transition-colors"
              >
                Terms of Service
              </Link>
              <span className="text-[var(--color-ink-subtle)]/40" aria-hidden="true">·</span>
              <Link
                to="/support"
                onClick={onClose}
                className="hover:text-[var(--color-ink-main)] transition-colors"
              >
                Contact Support
              </Link>
            </div>

            {/* App Version - Only show in Electron */}
            {isElectron() && appVersion && (
              <div className="mt-6 text-center text-xs text-[var(--color-ink-light)]">
                Version {appVersion}
              </div>
            )}
          </div>

          {showDeleteModal && (
            <DeleteAccountModal
              onClose={() => setShowDeleteModal(false)}
              apiUrl={apiUrl}
              onDeleteSuccess={() => {
                localStorage.removeItem('token');
                window.location.href = '/';
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default EnhancedSettingsModal;